import { useCallback, useEffect } from 'react';
import { useInstallStore } from '@/stores/installStore';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

export type InstallSupport = 'available' | 'installed' | 'unsupported';
export type Browser = 'chrome' | 'edge' | 'safari-ios' | 'firefox' | 'samsung' | 'other';

/**
 * Mount once at the App root. Captures `beforeinstallprompt` and `appinstalled`
 * before any code-split route has mounted, so the deferred prompt isn't lost.
 */
export function useInstallPromptCapture() {
  const setDeferred = useInstallStore((s) => s.setDeferred);
  const markInstalled = useInstallStore((s) => s.markInstalled);

  useEffect(() => {
    const onBefore = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => markInstalled();

    window.addEventListener('beforeinstallprompt', onBefore as EventListener);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBefore as EventListener);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, [setDeferred, markInstalled]);
}

/**
 * Read the current install state and trigger the native prompt.
 * Used from any feature page (e.g. Settings).
 */
export function useInstallPrompt() {
  const deferred = useInstallStore((s) => s.deferred);
  const installed = useInstallStore((s) => s.installed);
  const setDeferred = useInstallStore((s) => s.setDeferred);
  const markInstalled = useInstallStore((s) => s.markInstalled);

  const promptInstall = useCallback(async (): Promise<'accepted' | 'dismissed' | 'unavailable'> => {
    if (!deferred) return 'unavailable';
    await deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === 'accepted') markInstalled();
    setDeferred(null);
    return outcome;
  }, [deferred, setDeferred, markInstalled]);

  const support: InstallSupport = installed ? 'installed' : deferred ? 'available' : 'unsupported';

  return { support, promptInstall, browser: detectBrowser() };
}

export function detectBrowser(): Browser {
  if (typeof window === 'undefined') return 'other';
  const ua = window.navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua) && !('MSStream' in window)) return 'safari-ios';
  if (/Edg\//.test(ua)) return 'edge';
  if (/SamsungBrowser/.test(ua)) return 'samsung';
  if (/Firefox\//.test(ua)) return 'firefox';
  if (/Chrome\//.test(ua)) return 'chrome';
  return 'other';
}
